import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import pb from '../lib/pocketbase/client';
import { useTenant } from './TenantContext';

const AuthContext = createContext();

const AUTH_EVENTS_STORAGE_KEY = 'saas_auth_events';
const MAX_AUTH_EVENTS = 25;

/**
 * Map a PocketBase SDK error to a friendly, actionable message.
 * Network failures (no HTTP status) are treated separately from API rejections.
 */
const describeAuthError = (err, context = 'generic') => {
  const status = err?.status ?? (err?.originalError?.status ?? null);
  const apiMessage = err?.response?.message || err?.message || '';

  // No status => request never reached the server (offline / PocketBase down)
  if (status === null || status === undefined) {
    return 'Unable to reach the authentication server. Please check your connection and try again.';
  }

  if (context === 'requestOTP' && (status === 400 || status === 404)) {
    return 'Email not registered. Please sign up or contact your organization admin.';
  }
  if (context === 'verifyOTP') {
    if (status === 400 || status === 404) {
      return 'Invalid or expired verification code. Please try again or resend the code.';
    }
  }
  if (status === 403 || status === 401) {
    return 'Access denied. Your account may be inactive or lack permissions.';
  }
  return apiMessage || 'Authentication failed. Please try again.';
};

const readAuthEvents = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_EVENTS_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { upsertOrganization, switchTenant } = useTenant();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authEvents, setAuthEvents] = useState(readAuthEvents);

  const logAuthEvent = useCallback((action, detail = '') => {
    setAuthEvents((prev) => {
      const next = [
        { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, action, detail, timestamp: new Date().toISOString() },
        ...prev
      ].slice(0, MAX_AUTH_EVENTS);
      try {
        localStorage.setItem(AUTH_EVENTS_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn('Failed to persist auth event log:', e);
      }
      return next;
    });
  }, []);

  /**
   * Sync the authenticated user's PocketBase organization record into the
   * local TenantContext so trade gating + branding react immediately.
   * Best-effort: failures (offline, missing expand) never block login.
   */
  const syncTenantFromUser = useCallback(
    async (authRecord) => {
      try {
        const orgId = authRecord?.organization_id || authRecord?.organizationId;
        if (!orgId || typeof orgId !== 'string') return;

        let orgRecord = authRecord.expand?.organization_id;
        if (!orgRecord || typeof orgRecord === 'string') {
          orgRecord = await pb.collection('organizations').getOne(orgId);
        }

        const mapped = {
          id: orgRecord.id,
          name: orgRecord.name || 'My Organization',
          slug: orgRecord.slug || orgRecord.id,
          plan: orgRecord.plan || 'Pro',
          trades: orgRecord.trades || [],
          accreditations: orgRecord.accreditations || [],
          gasSafeRegNumber: orgRecord.gasSafeRegNumber || '',
          oftecRegNumber: orgRecord.oftecRegNumber || '',
          niceicRegNumber: orgRecord.niceicRegNumber || '',
          napitRegNumber: orgRecord.napitRegNumber || '',
          branding: orgRecord.branding || {},
          members: orgRecord.members || [],
          remote: true
        };
        upsertOrganization(mapped);
        switchTenant(mapped.id);
      } catch (e) {
        console.warn('Tenant sync skipped (offline or organization unavailable):', e?.message || e);
      }
    },
    [upsertOrganization, switchTenant]
  );

  /**
   * Trigger PocketBase OTP dispatch for an email.
   * Returns { otpId } on success; throws Error with friendly message otherwise.
   */
  const requestEmailOTP = useCallback(async (email) => {
    setAuthError(null);
    try {
      const result = await pb.collection('users').requestOTP(email);
      logAuthEvent('OTP_REQUESTED', email);
      return { otpId: result?.otpId };
    } catch (err) {
      const message = describeAuthError(err, 'requestOTP');
      logAuthEvent('OTP_REQUEST_FAILED', `${email} — ${message}`);
      setAuthError(message);
      throw new Error(message);
    }
  }, [logAuthEvent]);

  /**
   * Verify the OTP code. On success PocketBase returns a JWT session which the
   * SDK stores automatically; we mirror it into context state.
   */
  const verifyEmailOTP = useCallback(async (otpId, code) => {
    setAuthError(null);
    try {
      const authData = await pb.collection('users').authWithOTP(otpId, code);
      const record = authData?.record || pb.authStore.record;
      logAuthEvent('LOGIN_OTP', record?.email || 'unknown user');
      syncTenantFromUser(record);
      return record;
    } catch (err) {
      const message = describeAuthError(err, 'verifyOTP');
      logAuthEvent('LOGIN_FAILED', message);
      setAuthError(message);
      throw new Error(message);
    }
  }, [logAuthEvent, syncTenantFromUser]);

  /**
   * Create a new organization + initial Owner user, then dispatch an OTP so the
   * signup flow can immediately verify & authenticate.
   * Returns { otpId, email } for the caller's OTP step.
   *
   * REMOVED (docs/signup.md §0/§3): orgs/users must NEVER be created from the
   * browser — `organizations`/`users` keep createRule: null and are provisioned
   * exclusively by the Node signup-service after Stripe payment confirmation.
   * Signup.jsx now calls POST /api/signup/checkout and redirects to Stripe;
   * SignupSuccess.jsx polls /api/signup/status and completes OTP login.
   */

  const logout = useCallback(() => {
    const email = pb.authStore.record?.email;
    pb.authStore.clear();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setAuthError(null);
    logAuthEvent('LOGOUT', email || 'unknown user');
  }, [logAuthEvent]);

  // Validate any persisted session on mount + react to external authStore changes.
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (pb.authStore.isValid && pb.authStore.record?.id) {
        setUser(pb.authStore.record);
        setToken(pb.authStore.token);
        setIsAuthenticated(true);
        try {
          // Re-validate the JWT against the server; stale tokens are dropped.
          const refreshed = await pb.collection('users').authRefresh();
          if (!cancelled && refreshed?.record) {
            setUser(refreshed.record);
          }
        } catch (e) {
          // 401 => expired/revoked; network errors keep the offline session.
          if (e?.status === 401 || e?.status === 403) {
            pb.authStore.clear();
            if (!cancelled) {
              setUser(null);
              setToken(null);
              setIsAuthenticated(false);
            }
          }
        }
      }
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    initialize();

    const unsubscribe = pb.authStore.onChange((newToken, record) => {
      if (cancelled) return;
      setToken(newToken || null);
      setUser(record || null);
      setIsAuthenticated(Boolean(newToken && record));
    });

    return () => {
      cancelled = true;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        authError,
        authEvents,
        requestEmailOTP,
        verifyEmailOTP,
        logout,
        clearAuthError: () => setAuthError(null)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
